// src/pages/school_exams/template_builder/PropertiesPanel.js
import React from "react";
import {
  Box,
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
} from "@mui/material";

const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1123;

const PropertiesPanel = ({ selected, updateElement, deleteElement }) => {
  if (!selected) {
    return (
      <Box
        sx={{
          width: 260,
          background: "#f9fafb",
          p: 2,
          borderLeft: "1px solid #e5e7eb",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Properties
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select an element to edit its properties.
        </Typography>
      </Box>
    );
  }

  const update = (changes) => updateElement(selected.id, changes);

  const centerOffsetX = Math.round(
    (selected.x || 0) + (selected.w || 0) / 2 - PAGE_WIDTH / 2
  );
  const centerOffsetY = Math.round(
    (selected.y || 0) + (selected.h || 0) / 2 - PAGE_HEIGHT / 2
  );

  return (
    <Box
      sx={{
        width: 260,
        background: "#f9fafb",
        p: 2,
        borderLeft: "1px solid #e5e7eb",
        overflowY: "auto",
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        Edit {selected.type.toUpperCase()}
      </Typography>

      {/* POSITION + SIZE */}
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        Position & Size
      </Typography>
      <Box sx={{ display: "flex", gap: 1, mt: 0.5, mb: 1.5 }}>
        <TextField
          label="X"
          type="number"
          size="small"
          value={Math.round(selected.x)}
          onChange={(e) => update({ x: Number(e.target.value) || 0 })}
        />
        <TextField
          label="Y"
          type="number"
          size="small"
          value={Math.round(selected.y)}
          onChange={(e) => update({ y: Number(e.target.value) || 0 })}
        />
        <TextField
          label="W"
          type="number"
          size="small"
          value={Math.round(selected.w)}
          onChange={(e) => update({ w: Number(e.target.value) || 10 })}
        />
        <TextField
          label="H"
          type="number"
          size="small"
          value={Math.round(selected.h)}
          onChange={(e) => update({ h: Number(e.target.value) || 10 })}
        />
      </Box>

      <Typography variant="caption" sx={{ color: "#6b7280" }}>
        Center ΔX: {centerOffsetX}px | ΔY: {centerOffsetY}px{" "}
        {centerOffsetX === 0 && centerOffsetY === 0 && "(Perfect center)"}
      </Typography>

      {/* Center on page */}
      <Box sx={{ display: "flex", gap: 1, mt: 0.5, mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={() =>
            update({
              x: (PAGE_WIDTH - (selected.w || 0)) / 2,
            })
          }
        >
          CENTER X
        </Button>
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={() =>
            update({
              y: (PAGE_HEIGHT - (selected.h || 0)) / 2,
            })
          }
        >
          CENTER Y
        </Button>
      </Box>

      {/* TEXT PROPS */}
      {selected.type === "text" && (
        <>
          {/* Editable text */}
          <TextField
            label="Text"
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
            value={selected.text || ""}
            onChange={(e) => update({ text: e.target.value })}
          />

          <TextField
            label="Font Size"
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            value={selected.fontSize || 18}
            onChange={(e) =>
              update({ fontSize: Number(e.target.value) || 18 })
            }
          />

          <ToggleButtonGroup
            value={selected.bold ? "bold" : "normal"}
            exclusive
            fullWidth
            sx={{ mb: 2 }}
            onChange={(e, val) => update({ bold: val === "bold" })}
          >
            <ToggleButton value="normal">Normal</ToggleButton>
            <ToggleButton value="bold">Bold</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">Font Color</Typography>
            <input
              type="color"
              value={selected.color || "#111827"}
              onChange={(e) => update({ color: e.target.value })}
              style={{ width: "100%", height: 35, border: "none" }}
            />
          </Box>

          <Typography variant="caption">Text Align</Typography>
          <ToggleButtonGroup
            value={selected.align || "left"}
            exclusive
            fullWidth
            sx={{ mb: 2 }}
            onChange={(e, val) => update({ align: val || "left" })}
          >
            <ToggleButton value="left">LEFT</ToggleButton>
            <ToggleButton value="center">CENTER</ToggleButton>
            <ToggleButton value="right">RIGHT</ToggleButton>
          </ToggleButtonGroup>

          {/* DYNAMIC BINDING */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">Dynamic Data</Typography>
            <select
              style={{
                width: "100%",
                padding: "8px",
                marginTop: "4px",
                borderRadius: "4px",
                border: "1px solid #cbd5e1",
              }}
              value={selected.bindKey || ""}
              onChange={(e) => {
                const key = e.target.value;

                const placeholderMap = {
                  school_name: "SCHOOL NAME",
                  school_address: "SCHOOL ADDRESS",
                  school_code: "SCHOOL CODE",

                  student_name: "STUDENT NAME",
                  father_name: "FATHER NAME",
                  mother_name: "MOTHER NAME",
                  roll_no: "ROLL NO",
                  class_name: "CLASS",
                  section_name: "SECTION",
                  dob: "DATE OF BIRTH",
                  student_code: "STUDENT CODE",

                  exam_title: "EXAM TITLE",
                  percentage: "PERCENTAGE",
                  grade: "GRADE",
                };

                update({
                  bindKey: key,
                  text: key ? placeholderMap[key] : selected.text,
                });
              }}
            >
              <option value="">Static Text</option>

              <optgroup label="School Information">
                <option value="school_name">School Name</option>
                <option value="school_address">School Address</option>
                <option value="school_code">School Code</option>
              </optgroup>

              <optgroup label="Student Information">
                <option value="student_name">Student Name</option>
                <option value="father_name">Father Name</option>
                <option value="mother_name">Mother Name</option>
                <option value="roll_no">Roll Number</option>
                <option value="class_name">Class</option>
                <option value="section_name">Section</option>
                <option value="dob">Date of Birth</option>
                <option value="student_code">Student Code</option>
              </optgroup>

              <optgroup label="Exam Information">
                <option value="exam_title">Exam Title</option>
                <option value="percentage">Percentage</option>
                <option value="grade">Grade</option>
              </optgroup>
            </select>
          </Box>
        </>
      )}

      {/* PHOTO */}
      {selected.type === "photo" && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Student photo auto-bind করা থাকে।
          </Typography>

          <TextField
            label="Border Width"
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            value={selected.borderWidth || 1}
            onChange={(e) =>
              update({ borderWidth: Number(e.target.value) || 1 })
            }
          />

          <TextField
            label="Border Radius"
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            value={selected.borderRadius || 0}
            onChange={(e) =>
              update({ borderRadius: Number(e.target.value) || 0 })
            }
          />
        </>
      )}

      {/* LINE */}
      {selected.type === "line" && (
        <>
          <TextField
            label="Thickness"
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            value={selected.thickness || 2}
            onChange={(e) =>
              update({ thickness: Number(e.target.value) || 1 })
            }
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">Color</Typography>
            <input
              type="color"
              value={selected.color || "#000000"}
              onChange={(e) => update({ color: e.target.value })}
              style={{ width: "100%", height: 30, border: "none" }}
            />
          </Box>
        </>
      )}

      {/* TABLE */}
      {selected.type === "table" && (
        <>
          <TextField
            label="Header Height"
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            value={selected.headerHeight || 32}
            onChange={(e) =>
              update({ headerHeight: Number(e.target.value) || 24 })
            }
          />

          <TextField
            label="Row Height"
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            value={selected.rowHeight || 26}
            onChange={(e) =>
              update({ rowHeight: Number(e.target.value) || 20 })
            }
          />

          <TextField
            label="Font Size"
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            value={selected.fontSize || 11}
            onChange={(e) =>
              update({ fontSize: Number(e.target.value) || 11 })
            }
          />

          {/* COLORS */}
          <Box
            sx={{
              border: "1px solid #e5e7eb",
              p: 1,
              mb: 2,
              borderRadius: 1,
              bgcolor: "white",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Table Colors
            </Typography>

            <Box sx={{ mt: 1, mb: 1 }}>
              <Typography variant="caption">Header Background</Typography>
              <input
                type="color"
                value={selected.headerBgColor || "#00c851"}
                onChange={(e) => update({ headerBgColor: e.target.value })}
                style={{ width: "100%", height: 30, border: "none" }}
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <Typography variant="caption">Header Text Color</Typography>
              <input
                type="color"
                value={selected.headerTextColor || "#000000"}
                onChange={(e) => update({ headerTextColor: e.target.value })}
                style={{ width: "100%", height: 30, border: "none" }}
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <Typography variant="caption">Row Background</Typography>
              <input
                type="color"
                value={selected.rowBgColor || "#ffffff"}
                onChange={(e) => update({ rowBgColor: e.target.value })}
                style={{ width: "100%", height: 30, border: "none" }}
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <Typography variant="caption">Alt Row Background</Typography>
              <input
                type="color"
                value={selected.altRowBgColor || "#f9fafb"}
                onChange={(e) => update({ altRowBgColor: e.target.value })}
                style={{ width: "100%", height: 30, border: "none" }}
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <Typography variant="caption">Border Color</Typography>
              <input
                type="color"
                value={selected.borderColor || "#000000"}
                onChange={(e) => update({ borderColor: e.target.value })}
                style={{ width: "100%", height: 30, border: "none" }}
              />
            </Box>

            <TextField
              label="Border Width"
              type="number"
              fullWidth
              sx={{ mb: 1 }}
              value={selected.borderWidth || 0.5}
              onChange={(e) =>
                update({ borderWidth: Number(e.target.value) || 0.5 })
              }
            />
          </Box>

          {/* COLUMNS */}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Columns
          </Typography>

          {selected.columns.map((col, idx) => (
            <Box
              key={col.id}
              sx={{
                border: "1px solid #e5e7eb",
                p: 1,
                mt: 1,
                borderRadius: 1,
                bgcolor: "white",
              }}
            >
              <TextField
                size="small"
                label="Label"
                fullWidth
                sx={{ mb: 1 }}
                value={col.label}
                onChange={(e) => {
                  const cols = [...selected.columns];
                  cols[idx] = { ...cols[idx], label: e.target.value };
                  update({ columns: cols });
                }}
              />

              <TextField
                size="small"
                label="Width"
                type="number"
                fullWidth
                sx={{ mb: 1 }}
                value={col.width}
                onChange={(e) => {
                  const cols = [...selected.columns];
                  cols[idx] = {
                    ...cols[idx],
                    width: Number(e.target.value) || 50,
                  };
                  update({ columns: cols });
                }}
              />

              <Button
                size="small"
                color="error"
                onClick={() => {
                  const cols = selected.columns.filter((_, i) => i !== idx);
                  update({ columns: cols });
                }}
              >
                Delete Column
              </Button>
            </Box>
          ))}

          <Button
            variant="outlined"
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            onClick={() =>
              update({
                columns: [
                  ...selected.columns,
                  { id: Date.now().toString(), label: "New", width: 80 },
                ],
              })
            }
          >
            Add Column
          </Button>

          {/* ROWS (sample preview rows) */}
          <Typography variant="subtitle2">Rows</Typography>

          {selected.rows.map((row, rIndex) => (
            <Box
              key={row.id}
              sx={{
                border: "1px solid #e5e7eb",
                p: 1,
                mt: 1,
                borderRadius: 1,
                bgcolor: "white",
              }}
            >
              {selected.columns.map((col, cIndex) => (
                <TextField
                  key={col.id}
                  size="small"
                  label={col.label}
                  fullWidth
                  sx={{ mb: 1 }}
                  value={row.cells[cIndex] || ""}
                  onChange={(e) => {
                    const rows = selected.rows.map((r) => ({ ...r }));
                    rows[rIndex].cells[cIndex] = e.target.value;
                    update({ rows });
                  }}
                />
              ))}

              <Button
                size="small"
                color="error"
                onClick={() => {
                  const rows = selected.rows.filter((_, i) => i !== rIndex);
                  update({ rows });
                }}
              >
                Delete Row
              </Button>
            </Box>
          ))}

          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={() =>
              update({
                rows: [
                  ...selected.rows,
                  {
                    id: Date.now().toString(),
                    cells: new Array(selected.columns.length).fill(""),
                  },
                ],
              })
            }
          >
            Add Row
          </Button>
        </>
      )}

      {/* SHAPES */}
      {(selected.type === "rect" || selected.type === "circle") && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">Fill Color</Typography>
            <input
              type="color"
              value={selected.fill || "#cccccc"}
              onChange={(e) => update({ fill: e.target.value })}
              style={{ width: "100%", height: 30, border: "none" }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">Border Color</Typography>
            <input
              type="color"
              value={selected.stroke || "#000000"}
              onChange={(e) => update({ stroke: e.target.value })}
              style={{ width: "100%", height: 30, border: "none" }}
            />
          </Box>

          <TextField
            label="Border Width"
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            value={selected.strokeWidth || 1}
            onChange={(e) =>
              update({ strokeWidth: Number(e.target.value) || 0 })
            }
          />
        </>
      )}

      <Button
        fullWidth
        color="error"
        variant="contained"
        sx={{ mt: 3 }}
        onClick={() => deleteElement(selected.id)}
      >
        Delete Element
      </Button>
    </Box>
  );
};

export default PropertiesPanel;
